import * as Yup from "yup";

import { Link, Redirect, useLocation, useHistory } from "react-router-dom";

import AuthLayout, { Divider, FormHeader } from "components/auth/AuthLayout";
import Button from "components/global/Button";
import CheckBox from "components/global/CheckBox";
import FormGroup from "components/global/FormGroup";
import { Formik } from "formik";
import Spacer from "components/global/Spacer";
import { connect } from "react-redux";
import { axiosInstance, loginUser, setLoggedIn } from "api";
import { extractEmails, showAlert } from "utils/utils";
import styled from "styled-components";
import { useEffect, useState } from "react";
import Logo from "components/global/Logo";
import usePageTitle from "hooks/usePageTitle";
import { store } from "api/store";
import { SET_SHOW_APP_MODAL } from "api/actions/types";
import ChevronLeft from "icons/ChevronLeft";

const FormWrapper = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 2.4rem;

  .spanFull {
    grid-column: 1/3;
  }
`;

const LoginWithEmail = ({ logged_in, user_accounts, user }) => {
  usePageTitle("Sign in");
  const history = useHistory();
  const { search } = useLocation();
  const email = extractEmails(search)?.[0];
  const add_account = new URLSearchParams(search).get("add_account");
  const redirectPath = new URLSearchParams(search).get("return_to");
  const error = new URLSearchParams(search).get("error");
  const postpaddyMobile = new URLSearchParams(search).get("postpaddy-mobile");
  const [loginEmail, setLoginEmail] = useState("");
  const [isSendingCode, setIsSendingCode] = useState(false);

  const handleLogin = async (cred) => {
    const userData = {
      email: loginEmail,
      password: cred.password,
    };
    const res = await loginUser(userData);

    if (res?.data?.status === false || res?.data?.errors) {
      showAlert({
        msg: res?.data?.message ?? "Incorrect email or password",
        type: "danger",
      });

      return;
    }
  };

  const handleRememberMe = (username) => {
    if (document.getElementById("rememberMe")?.checked && username) {
      localStorage.setItem("username", username);
    } else {
      localStorage.removeItem("username");
    }
  };

  const schema = Yup.object({
    password: Yup.string().required("Field required"),
  });

  const emailValidationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Field required"),
  });

  const handleCheckEmail = async (values, setSubmitting) => {
    try {
      const res = await axiosInstance.post(`login`, values);
      if (res.data?.status === true) {
        setLoginEmail(values.email);
        handleRememberMe(values.email);
      } else {
        showAlert({
          msg: res.data?.message ?? "Incorrect email",
          type: "danger",
        });
      }
      setSubmitting(false);
    } catch (error) {
      showAlert({
        msg: error.response?.data?.message ?? "Incorrect email",
        type: "danger",
      });
      setSubmitting(false);
    }
  };

  const requestLoginCode = async () => {
    setIsSendingCode(true);
    try {
      const res = await axiosInstance.post(`get-login-code`, {
        email: loginEmail,
      });
      if (res.data.status === true) {
        showAlert({
          msg: res.data?.message ?? "Login Code has been sent to your email!",
          type: "success",
        });
        if (postpaddyMobile) {
          history.push(
            `/sign-in/with-code?login_email=${encodeURIComponent(
              loginEmail
            )}&postpaddy-mobile=${encodeURIComponent(postpaddyMobile)}`
          );
        } else {
          history.push(
            `/sign-in/with-code?login_email=${encodeURIComponent(loginEmail)}${
              redirectPath
                ? `&return_to=${encodeURIComponent(redirectPath)}`
                : ""
            }`
          );
        }
      }
      setIsSendingCode(false);
    } catch (error) {
      setIsSendingCode(false);
      showAlert({
        msg: error.response?.data?.message ?? "An error occurred!",
        type: "danger",
      });
    }
  };

  useEffect(() => {
    const rmCheck = document.getElementById("rememberMe");

    if (error && !logged_in) {
      showAlert({
        msg: error,
        type: "danger",
      });
    }

    if (localStorage.username && localStorage.username !== "") {
      rmCheck?.click();
    }
  }, [error, logged_in]);

  const redirectTo = !user?.has_viewed_tooltips?.onboarding_completed
    ? "/onboarding/create-brand"
    : !user_accounts?.length && !redirectPath?.includes("accept-invite")
    ? "/onboarding/create-brand"
    : user_accounts?.length && !redirectPath?.includes("accept-invite")
    ? redirectPath ?? "/dashboard/home"
    : "/dashboard/home";

  useEffect(() => {
    if (redirectTo.includes("dashboard")) {
      store.dispatch({
        type: SET_SHOW_APP_MODAL,
        payload: true,
      });
    }
  }, [redirectTo]);

  if (user?.email_verified_at && redirectPath?.includes("accept-invite")) {
    return <Redirect to={redirectPath ?? "/dashboard/home"} />;
  }

  if (!add_account && logged_in && user_accounts !== null) {
    return <Redirect to={redirectTo} />;
  }

  return (
    <AuthLayout>
      <FormHeader>
        <Logo />
        <p className="l3">
          Don't have an account?{" "}
          <Link
            to={
              redirectPath
                ? `/sign-up${search}`
                : postpaddyMobile
                ? "/sign-up?postpaddy-mobile=true"
                : "/sign-up"
            }
            className="signInText l3"
          >
            Sign up
          </Link>
        </p>
      </FormHeader>
      <Spacer y={6} yMobile={3.6} />
      <h3 className="title colorGrey1">
        {!loginEmail ? "Sign in to PostPaddy" : "Welcome back"}
      </h3>
      <Spacer y={0.6} />
      {!loginEmail ? (
        <p className="description">
          Manage and schedule posts on mutiple social media accounts.
        </p>
      ) : (
        <p className="description">
          Let's get you back into your account, {loginEmail}!
        </p>
      )}

      {loginEmail && (
        <>
          <Spacer y={2.4} />
          <div style={{ marginBottom: "24px" }}>
            <Button
              text="Request login code"
              className="borderedGrey"
              fullWidth
              onClick={requestLoginCode}
              disabled={isSendingCode}
              loading={isSendingCode}
            />
          </div>
        </>
      )}
      {loginEmail && (
        <Divider>
          <span className="l3 text">or</span>
        </Divider>
      )}
      <Spacer y={2.4} />
      {/* EMAIL FORM */}
      {!loginEmail && (
        <Formik
          initialValues={{
            email: (email || localStorage.username) ?? "",
          }}
          validationSchema={emailValidationSchema}
          onSubmit={(values, { setSubmitting }) => {
            handleCheckEmail(values, setSubmitting);
          }}
        >
          {({ handleSubmit, isSubmitting, isValid, values }) => (
            <FormWrapper onSubmit={handleSubmit}>
              <FormGroup
                fieldStyle="shortText"
                type="email"
                label="Email address"
                name="email"
                className="spanFull"
              />
              <div className="flexRow justifySpaceBetween spanFull">
                <CheckBox
                  id="rememberMe"
                  name="rememberMe"
                  label="Remember me"
                  value="rememberMe"
                />
              </div>

              <Button
                type="submit"
                text="Continue"
                className="spanFull"
                disabled={isSubmitting || !isValid || !values.email}
                loading={isSubmitting}
                fullWidth
              />
            </FormWrapper>
          )}
        </Formik>
      )}

      {loginEmail && (
        <Formik
          initialValues={{
            password: "",
          }}
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting }) => {
            setSubmitting(true);
            await handleLogin(values);
            setSubmitting(false);
          }}
        >
          {({ handleSubmit, isSubmitting, isValid, values }) => (
            <FormWrapper onSubmit={handleSubmit}>
              <FormGroup
                fieldStyle="shortText"
                type="password"
                label="Password"
                name="password"
                className="password spanFull"
              />

              <Button
                type="submit"
                text="Sign in"
                className="spanFull"
                disabled={isSubmitting || !isValid || !values.password}
                loading={isSubmitting}
                fullWidth
              />
              <div className="flexRow justifyCenter spanFull">
                <Link
                  to="/password-reset"
                  className="colorSecondary forgotPassword"
                >
                  Forgot password?
                </Link>
              </div>
            </FormWrapper>
          )}
        </Formik>
      )}
      <Spacer y={2.4} />
      <div className="flexRow alignCenter justifyCenter">
        <Button
          text="Go back"
          iconComponent={<ChevronLeft size={20} color="#032250" />}
          className="noBorder"
          onClick={() => history.goBack()}
        />
      </div>
    </AuthLayout>
  );
};

const mapStateToProps = (state) => {
  return {
    logged_in: state.logged_in,
    user_accounts: state.user_accounts,
    user: state.user,
    user_token: state.user_token,
    refresh_token: state.refresh_token,
    expires_in: state.expires_in,
  };
};

export default connect(mapStateToProps)(LoginWithEmail);
